<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import SearchAPI from 'dashboard/api/search';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const query = ref('');
const results = ref([]);
const activeIndex = ref(0);
const isLoading = ref(false);
let debounceTimer;

const conversationId = computed(() => store.getters.getSelectedChat?.id);
const resultLabel = computed(() => {
  if (!query.value.trim() || isLoading.value) return '';
  if (!results.value.length) return t('CONVERSATION.SEARCH.NO_MATCHING_RESULTS');
  return t('CONVERSATION.SEARCH.RESULT_COUNT', {
    current: activeIndex.value + 1,
    total: results.value.length,
  });
});

const focusResult = async () => {
  const message = results.value[activeIndex.value];
  if (!message) return;

  await store.dispatch('fetchPreviousMessages', {
    conversationId: conversationId.value,
    after: Math.max(message.id - 50, 0),
    before: message.id + 50,
  });
  await router.replace({
    query: { ...route.query, messageId: message.id },
  });
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId: message.id });
};

const search = async () => {
  const searchTerm = query.value.trim();
  if (searchTerm.length < 2) {
    results.value = [];
    return;
  }

  isLoading.value = true;
  try {
    const { data } = await SearchAPI.messages({
      q: searchTerm,
      conversationId: conversationId.value,
    });
    results.value = data.payload.messages;
    activeIndex.value = 0;
    await focusResult();
  } finally {
    isLoading.value = false;
  }
};

const move = async direction => {
  if (!results.value.length) return;
  activeIndex.value =
    (activeIndex.value + direction + results.value.length) % results.value.length;
  await focusResult();
};

const clear = () => {
  query.value = '';
  results.value = [];
  const nextQuery = { ...route.query };
  delete nextQuery.messageId;
  router.replace({ query: nextQuery });
};

watch(query, () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(search, 300);
});

watch(conversationId, clear);
onBeforeUnmount(() => clearTimeout(debounceTimer));
</script>

<template>
  <div class="mx-2 mb-2">
    <div
      class="flex items-center w-full h-10 gap-1 px-3 border rounded-xl bg-n-solid-1 border-n-weak focus-within:border-n-brand focus-within:ring-1 focus-within:ring-n-brand"
    >
      <Icon icon="i-lucide-search" class="shrink-0 size-4 text-n-slate-10" />
      <input
        v-model="query"
        type="search"
        :placeholder="$t('CONVERSATION.SEARCH.IN_CONVERSATION_PLACEHOLDER')"
        class="flex-1 min-w-0 h-full px-2 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-9 focus:ring-0"
        @keydown.enter.prevent="move(1)"
      />
      <template v-if="query.trim()">
        <span
          v-if="resultLabel"
          class="px-1 text-xs whitespace-nowrap text-n-slate-10"
        >
          {{ resultLabel }}
        </span>
        <div class="w-px h-5 mx-1 bg-n-weak" />
        <button
          type="button"
          class="grid bg-transparent border-0 rounded-md shrink-0 size-7 place-content-center hover:bg-n-alpha-2 disabled:opacity-40"
          :disabled="!results.length"
          :aria-label="$t('CONVERSATION.SEARCH.PREVIOUS_RESULT')"
          @click="move(-1)"
        >
          <Icon icon="i-lucide-chevron-up" class="size-4" />
        </button>
        <button
          type="button"
          class="grid bg-transparent border-0 rounded-md shrink-0 size-7 place-content-center hover:bg-n-alpha-2 disabled:opacity-40"
          :disabled="!results.length"
          :aria-label="$t('CONVERSATION.SEARCH.NEXT_RESULT')"
          @click="move(1)"
        >
          <Icon icon="i-lucide-chevron-down" class="size-4" />
        </button>
        <button
          type="button"
          class="grid bg-transparent border-0 rounded-md shrink-0 size-7 place-content-center hover:bg-n-alpha-2"
          :aria-label="$t('CONVERSATION.SEARCH.CLEAR')"
          @click="clear"
        >
          <Icon icon="i-lucide-x" class="size-4" />
        </button>
      </template>
    </div>
  </div>
</template>
